import { ReactNode, useMemo } from "react"

import { defaultSort, limitAndOffset } from "api/utils"

import { Column } from "react-table"
import { EmptyState } from "components/EmptyState"
import { Supplier } from "models/supplier"
import Surface from "components/Surface"
import { Table } from "components/tables"
import { ReactComponent as VendorsIcons } from "assets/icon-client.svg"
import { css } from "@emotion/css"
import { fetchSuppliers } from "api/suppliers"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"
import { useQuery } from "react-query"
import { useAgencyId } from "hooks/useAgencyId"

const styles = {
  surface: css`
    padding: 0;
  `,
}

const sortByDefault = "updated_date,DESC"

type URLSearchParamsInit =
  | string
  | string[][]
  | Record<string, string>
  | URLSearchParams
  | undefined

const mergeParams =
  (other: URLSearchParamsInit) => (params: URLSearchParams) => {
    const otherParams = new URLSearchParams(other)
    const merged = Object.assign(
      {},
      Object.fromEntries(params),
      Object.fromEntries(otherParams),
    )
    return new URLSearchParams(merged)
  }

export type VendorsListProps = {
  queryKey: string
  params?: URLSearchParamsInit
  columns: Column<Supplier>[]
  tableAction?: ReactNode
  isFromConnectedVendors?: boolean
}

export default function VendorsList(props: VendorsListProps) {
  const { queryKey, columns, params = undefined, tableAction } = props
  const { t } = useI18n()
  const ls = useLocationSearch()

  const query_params = ls
    .map(limitAndOffset)
    .map(defaultSort(sortByDefault))
    .map(mergeParams(params))

  const { agencyId } = useAgencyId()
  agencyId && query_params.append("agencyId", agencyId)

  const { data, isLoading, updatedAt } = useQuery(
    [queryKey, query_params.toString()],
    fetchSuppliers,
  )

  const rows = useMemo(() => {
    return data?.data ?? []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt])

  return (
    <Surface className={styles.surface}>
      <div>
        <Table
          id={`${queryKey}-list-table-id`}
          columns={columns}
          data={rows}
          filters={[]}
          total={data?.total ?? 0}
          loading={isLoading}
          searchBy="search"
          noResults={
            <EmptyState
              icon={<VendorsIcons />}
              title={t("vendors.list.empty")}
            />
          }
          actionButton={tableAction}
        />
      </div>
    </Surface>
  )
}
